import type { UUID } from '@protector/utils/types';
import type { ProductType } from '@protector/utils/constants';
import { protectorApiClient, type RequestProps } from '@protector/api-client';
import type {
  DiseaseRiskAccumulatedRainfallResponse,
  DiseaseRisksDto,
  DiseaseRisksPotatoDto,
  GeometryLocation,
  HourWeatherData,
  HourWeatherDataDTO,
  LastSprays,
  RecommendationNoteGroup,
  RecommendationNoteGroupRequest,
  RecommendationNotesResponse,
  PostRecommendationNotesResponse
} from './models.ts';
import { diseaseRisksDtoSchema, diseaseRisksPotatoDtoSchema } from './schemas.ts';

const emptyHourWeatherData: HourWeatherData[] = [];

function requestWithDomain<I, O>(args: Omit<RequestProps<I, O>, 'domain'>) {
  return protectorApiClient.request<I, O>({ ...args, domain: 'DiseaseRisk' });
}

export function getLastSprayRegistration(propertyId: UUID, productType: ProductType): Promise<LastSprays | undefined> {
  return requestWithDomain<unknown, LastSprays | undefined>({
    config: {
      method: 'GET',
      url: `/v1/timeline/properties/${propertyId}/sprays/last?product_type=${productType}`
    }
  }).then(response => response.data);
}

export function getAccumulatedRainfall(
  orgId: string,
  location: GeometryLocation,
  timezone: string
): Promise<DiseaseRiskAccumulatedRainfallResponse> {
  return requestWithDomain<unknown, DiseaseRiskAccumulatedRainfallResponse>({
    config: {
      method: 'POST',
      url: '/v1/analytics/disease-risk/rainfall-data/accumulated',
      data: { location, orgId, timezone }
    }
  }).then(response => response.data);
}

export async function getHourWeatherData(date: string, countryCode: string, coords: number[]): Promise<HourWeatherData[]> {
  if (!date || !countryCode || coords.length === 0) {
    return emptyHourWeatherData;
  }

  try {
    const response = await requestWithDomain<unknown, HourWeatherDataDTO[]>({
      config: {
        method: 'POST',
        url: '/v2/spray-status/hourly-clock',
        data: { location: coords, country_code: countryCode, date }
      }
    });

    const data = response.data;
    return data.map(
      dayDatum =>
        dayDatum &&
        ({
          sprayStatus: dayDatum?.spray_status ?? null,
          weatherIconKey: dayDatum?.weather_icon_key ?? null,
          humidityPercentage: dayDatum?.humidity_percentage ?? null,
          temperature: dayDatum?.temperature ?? null,
          rainfallAmount: dayDatum?.rainfall_amount ?? null,
          rainfallProbability: dayDatum?.rainfall_probability ?? null,
          windSpeedMps: dayDatum?.wind_speed ?? null,
          windGustMps: dayDatum?.wind_gust ?? null,
          windDirection: dayDatum?.wind_direction ?? null
        } as HourWeatherData)
    );
  } catch {
    return emptyHourWeatherData;
  }
}

export function getRecommendationNotes(propertyId: string, seasonId: string): Promise<RecommendationNotesResponse[]> {
  return requestWithDomain<unknown, RecommendationNotesResponse[]>({
    config: {
      method: 'GET',
      url: `/v2/properties/${propertyId}/disease-risks/recommendation-notes?seasonId=${seasonId}`
    }
  }).then(response => response.data);
}

export function postRecommendationNotes(
  propertyId: string,
  note: { notes: string; season_id: string }
): Promise<PostRecommendationNotesResponse> {
  return requestWithDomain<unknown, PostRecommendationNotesResponse>({
    config: {
      method: 'POST',
      url: `/v2/properties/${propertyId}/disease-risks/recommendation-notes`,
      data: note
    }
  }).then(response => response.data);
}

export function getRecommendationNotesGroup(seasonId: string): Promise<RecommendationNoteGroup[]> {
  return requestWithDomain<unknown, RecommendationNoteGroup[]>({
    config: {
      method: 'GET',
      url: `/v2/seasons/${seasonId}/disease-risk/recommendation-notes-group`
    }
  }).then(response => response.data);
}

export function postRecommendationNotesGroup(seasonId: string, data: RecommendationNoteGroupRequest): Promise<RecommendationNoteGroup> {
  return requestWithDomain<unknown, RecommendationNoteGroup>({
    config: {
      method: 'POST',
      url: `/v2/seasons/${seasonId}/disease-risk/recommendation-notes-group`,
      data
    }
  }).then(response => response.data);
}

export function putRecommendationNotesGroup(
  seasonId: string,
  groupId: string,
  data: RecommendationNoteGroupRequest
): Promise<RecommendationNoteGroup> {
  return requestWithDomain<unknown, RecommendationNoteGroup>({
    config: {
      method: 'PUT',
      url: `/v2/seasons/${seasonId}/disease-risk/recommendation-notes-group/${groupId}`,
      data
    }
  }).then(response => response.data);
}

interface GetDiseaseRisksByPropertyProps {
  propertyId: UUID;
  fieldIds?: UUID[];
  mockedData?: boolean;
}

export async function getDiseaseRisksByProperty({
  propertyId,
  fieldIds,
  mockedData
}: GetDiseaseRisksByPropertyProps): Promise<DiseaseRisksDto> {
  const headers: Record<string, string> = {};
  if (mockedData) headers['x-demo'] = 'true';

  const params = fieldIds?.length ? new URLSearchParams(fieldIds.map(id => ['fieldIds', id])) : undefined;

  const response = await requestWithDomain({
    config: {
      method: 'GET',
      url: `/v2/properties/${propertyId}/disease-risks`,
      headers,
      params
    },
    outputSchema: diseaseRisksDtoSchema
  });

  return {
    ...response.data,
    hasShiftData: response.headers['x-has-shift-data'] === 'true'
  };
}

export async function getDiseaseRiskPotatoes(propertyId: UUID, mockedData?: boolean): Promise<DiseaseRisksPotatoDto> {
  const headers: Record<string, string> = {};
  if (mockedData) headers['x-demo'] = 'true';

  const response = await requestWithDomain({
    config: {
      method: 'GET',
      url: `/v2/properties/${propertyId}/disease-risks/potato`,
      headers
    },
    outputSchema: diseaseRisksPotatoDtoSchema
  });

  return response.data;
}
